import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { ChevronDownIcon, PlusIcon } from "@heroicons/react/24/outline";
import { PageHeader, PageHeaderWithBack } from "@/components/common";
import { useJob } from "../hooks/useJob";
import { useDilapidation } from "./hooks/useDilapidation";
import { DilapidationSectionCard } from "./components/DilapidationSectionCard";
import { useAlert } from "@/hooks/useAlert";
import { dilapidationStatusDisplayLabel, dilapidationStatusTargetActionLabel, } from "./dilapidationStatusLabels";
import styles from "./DilapidationReportEdit.module.css";
export const DilapidationReportEdit = () => {
    const { jobId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { confirm, toast } = useAlert();
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    const { job } = useJob(jobId);
    const { dilapidation, loading, error, notFound, createDilapidation, updateDilapidation, updateStatus, uploadSectionDocument, deleteDocument, deleteDilapidation, } = useDilapidation(jobId);
    const [sections, setSections] = useState([]);
    const [statusMenuOpen, setStatusMenuOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [uploadingDocument, setUploadingDocument] = useState(false);
    const statusDropdownRef = useRef(null);
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (statusMenuOpen &&
                statusDropdownRef.current &&
                !statusDropdownRef.current.contains(e.target)) {
                setStatusMenuOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [statusMenuOpen]);
    useEffect(() => {
        if (!dilapidation?.id) {
            setSections([]);
            return;
        }
        setSections(dilapidation.sections ?? []);
        // Intentionally depend only on report id: dilapidation.sections gets new references after
        // upload/delete in useDilapidation; re-syncing would wipe unsaved section text.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dilapidation?.id]);
    const handleBack = useCallback(() => {
        navigate(`${basePath}/jobs/${jobId}/edit`);
    }, [basePath, jobId, navigate]);
    const handleView = useCallback(() => {
        navigate(`${basePath}/jobs/${jobId}/dilapidation`);
    }, [basePath, jobId, navigate]);
    const handleCreate = useCallback(async () => {
        const created = await createDilapidation();
        if (created) {
            toast.success("Dilapidation report created.");
            setSections(created.sections ?? []);
        }
        else {
            toast.error("Failed to create dilapidation report.");
        }
    }, [createDilapidation, toast]);
    const handleSave = useCallback(async () => {
        if (!jobId || !dilapidation)
            return;
        setSaving(true);
        try {
            const sectionPayload = sections.map((s, i) => ({
                id: s.id ?? undefined,
                sectionName: s.sectionName ?? undefined,
                subHeading: s.subHeading ?? undefined,
                content: s.content ?? undefined,
                checkBox: s.checkBox ?? true,
                displayOrder: i,
            }));
            const updated = await updateDilapidation({ sections: sectionPayload });
            if (updated) {
                toast.success("Dilapidation report saved.");
                setSections(updated.sections ?? sections);
            }
            else {
                toast.error("Failed to save dilapidation report.");
            }
        }
        catch {
            toast.error("Failed to save dilapidation report.");
        }
        finally {
            setSaving(false);
        }
    }, [jobId, dilapidation, sections, updateDilapidation, toast]);
    const handleStatusChange = useCallback(async (status) => {
        setStatusMenuOpen(false);
        const updated = await updateStatus(status);
        if (updated) {
            toast.success(`Status updated to ${dilapidationStatusDisplayLabel(status)}.`);
        }
        else {
            toast.error("Failed to update status.");
        }
    }, [updateStatus, toast]);
    const handleAddSection = useCallback(() => {
        setSections((prev) => [
            ...prev,
            {
                id: "",
                dilapidationId: dilapidation?.id ?? "",
                sectionName: undefined,
                subHeading: undefined,
                content: undefined,
                checkBox: true,
                displayOrder: prev.length,
                documents: [],
            },
        ]);
    }, [dilapidation?.id]);
    const handleSectionUpdate = useCallback((index) => (updates) => {
        setSections((prev) => prev.map((s, i) => (i === index ? { ...s, ...updates } : s)));
    }, []);
    const handleDeleteReport = useCallback(async () => {
        const ok = await confirm("Are you sure you want to delete this dilapidation report? This cannot be undone.", "Delete Dilapidation Report", { confirmText: "Delete", cancelText: "Cancel" });
        if (!ok)
            return;
        const success = await deleteDilapidation();
        if (success) {
            toast.success("Dilapidation report deleted.");
            handleBack();
        }
        else {
            toast.error("Failed to delete dilapidation report.");
        }
    }, [confirm, deleteDilapidation, toast, handleBack]);
    const handleUploadDocument = useCallback(async (sectionId, file) => {
        setUploadingDocument(true);
        try {
            const doc = await uploadSectionDocument(sectionId, file);
            if (doc) {
                toast.success("Document uploaded.");
                setSections((prev) => prev.map((s) => s.id === sectionId
                    ? { ...s, documents: [...(s.documents ?? []), doc] }
                    : s));
            }
            else {
                toast.error("Failed to upload document.");
            }
        }
        finally {
            setUploadingDocument(false);
        }
    }, [uploadSectionDocument, toast]);
    const handleDeleteDocument = useCallback(async (documentId) => {
        const success = await deleteDocument(documentId);
        if (success) {
            toast.success("Document deleted.");
            setSections((prev) => prev.map((s) => ({
                ...s,
                documents: (s.documents ?? []).filter((d) => d.id !== documentId),
            })));
        }
        else {
            toast.error("Failed to delete document.");
        }
    }, [deleteDocument, toast]);
    if (loading && !dilapidation) {
        return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, {}), _jsx(PageHeaderWithBack, { title: "Dilapidation Report", onBack: handleBack, breadcrumb: "Jobs/Dilapidation Report" }), _jsx("p", { className: styles.loadingText, children: "Loading..." })] }));
    }
    if (notFound && !dilapidation) {
        return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, {}), _jsx(PageHeaderWithBack, { title: "Dilapidation Report", subtitle: job ? `Job #${job.jobNumber}` : undefined, onBack: handleBack, breadcrumb: "Jobs/Dilapidation Report" }), _jsxs("div", { className: styles.emptyState, children: [_jsx("h3", { className: styles.emptyStateTitle, children: "No dilapidation report yet" }), _jsx("button", { type: "button", className: styles.createBtn, onClick: handleCreate, children: "Create Dilapidation Report" })] })] }));
    }
    const currentStatus = dilapidation?.status ?? "Draft";
    const canChangeStatus = currentStatus === "Draft" || currentStatus === "Public";
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, {}), _jsx(PageHeaderWithBack, { title: "Edit Dilapidation Report", subtitle: job ? `Job #${job.jobNumber}` : undefined, onBack: handleBack, breadcrumb: "Jobs/Dilapidation Report" }), error && _jsx("p", { className: styles.errorText, children: error }), _jsx("div", { className: styles.headerRow, children: _jsxs("div", { className: styles.actionsRow, children: [_jsx("button", { type: "button", className: styles.saveBtn, onClick: handleSave, disabled: saving, children: saving ? "Saving..." : "Save" }), canChangeStatus && (_jsxs("div", { className: styles.statusDropdown, ref: statusDropdownRef, children: [_jsxs("button", { type: "button", className: styles.statusBtn, onClick: () => setStatusMenuOpen((o) => !o), children: ["Status: ", dilapidationStatusDisplayLabel(currentStatus), _jsx(ChevronDownIcon, { width: 16, height: 16 })] }), statusMenuOpen && (_jsxs("div", { className: styles.statusMenu, children: [_jsx("button", { type: "button", className: styles.statusOption, onClick: () => handleStatusChange("Draft"), children: dilapidationStatusTargetActionLabel("Draft") }), _jsx("button", { type: "button", className: styles.statusOption, onClick: () => handleStatusChange("Public"), children: dilapidationStatusTargetActionLabel("Public") })] }))] })), _jsx("button", { type: "button", className: styles.viewBtn, onClick: handleView, children: "View" }), _jsx("button", { type: "button", className: `${styles.deleteBtn} ${styles.deleteBtnEnd}`, onClick: handleDeleteReport, children: "Delete Report" })] }) }), dilapidation?.feedback && (_jsxs("div", { className: styles.feedbackSection, children: [_jsx("h3", { className: styles.feedbackSectionTitle, children: "Client Feedback" }), _jsx("p", { className: styles.feedbackText, children: dilapidation.feedback }), dilapidation.isApprove && (_jsx("p", { className: styles.approvedState, children: "Client has approved this report." }))] })), _jsxs("button", { type: "button", className: styles.addSectionBtn, onClick: handleAddSection, children: [_jsx(PlusIcon, { width: 18, height: 18 }), "Add New Section"] }), sections.map((section, index) => (_jsx(DilapidationSectionCard, { section: section, isEditMode: true, onUpdate: handleSectionUpdate(index), onDeleteDocument: handleDeleteDocument, onUploadDocument: handleUploadDocument }, section.id || `new-${index}`))), uploadingDocument && (_jsx("div", { className: styles.uploadOverlay, "aria-live": "polite", "aria-busy": "true", children: _jsxs("div", { className: styles.uploadOverlayContent, children: [_jsx("div", { className: styles.spinner }), _jsx("p", { children: "Uploading document..." })] }) }))] }));
};
